// Calendar popover anchored under a .field-box. Uses react-day-picker for
// the actual calendar grid; the design's .field-popover class wraps it so
// it matches the other field popovers visually.

import { DayPicker, type Matcher } from "react-day-picker";
import "react-day-picker/dist/style.css";

interface Props {
  selected?: Date;
  onSelect: (d: Date) => void;
  /** Days strictly before this date are disabled. */
  minDate?: Date;
  /** Days strictly after this date are disabled. */
  maxDate?: Date;
  /** Month to land on when no `selected`. Useful for DOB pickers — defaulting
   *  to today is silly when you want users on years past. */
  defaultMonth?: Date;
  /** Show a "Year ▾" dropdown — handy for DOB / passport-issue style fields. */
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
}

export function CalendarPicker({ selected, onSelect, minDate, maxDate, defaultMonth, captionLayout }: Props) {
  const disabledList: Matcher[] = [];
  if (minDate) disabledList.push({ before: minDate });
  if (maxDate) disabledList.push({ after: maxDate });
  const disabled = disabledList.length ? disabledList : undefined;
  return (
    <div className="field-popover" style={{ minWidth: 320 }} onMouseDown={(e) => e.stopPropagation()}>
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={(d) => d && onSelect(d)}
        disabled={disabled}
        weekStartsOn={1}
        showOutsideDays
        defaultMonth={defaultMonth || selected}
        captionLayout={captionLayout}
        startMonth={minDate}
        endMonth={maxDate}
      />
    </div>
  );
}

// Helpers used by the search form to keep dates in YYYY-MM-DD strings outside
// the picker, but pass real Date objects in/out of react-day-picker.
export function ymdToDate(ymd: string): Date {
  return new Date(ymd + "T00:00:00");
}
export function dateToYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
